import {Icon} from '@wandb/weave/components/Icon';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import {clsx} from 'clsx';
import * as d3 from 'd3';
import numeral from 'numeral';
import React from 'react';
import ReactMarkdown from 'react-markdown';

import {UsageType} from '../../../generated/graphql';
import {
  addDaysUTC,
  millisecondsToSeconds,
  NUM_SECONDS_IN_HOUR,
  secondsToHours,
  secondsToMinutes,
  subDaysUTC,
} from '../../../util/date';
import {
  customLinkRenderer,
  getFormattedBillingDate,
  METER_SECTION_STYLE,
} from '../../Billing/AccountSettings/BillingTab/PlanCard/PlanCardComponents';
import {MeterBar} from '../../Billing/AccountSettings/ManageUsagePage/MeterBar';

export const DEFAULT_NUMBER_OF_DAYS = 30;

export const TODAY = new Date();
// Ignore local time zone
TODAY.setUTCHours(0, 0, 0, 0);
export const TOMORROW = addDaysUTC(TODAY, 1);
export const DEFAULT_DAYS_AGO = subDaysUTC(TOMORROW, DEFAULT_NUMBER_OF_DAYS);
export const DEFAULT_INTERVAL_MARKERS = [DEFAULT_DAYS_AGO, TODAY];

export type Props = {
  value: number;
  limit?: number;
  title: string;
  billingPeriodStart?: Date;
  billingPeriodEnd?: Date;
  button?: React.ReactNode;
  usageType: UsageType;
  className?: string;
  hideDateInterval?: boolean;
  costText?: string | React.ReactNode;
  isMonthly?: boolean;
  isProPlan?: boolean;
  isTrial: boolean;
  isUnlimited: boolean;
  secondaryButton?: React.ReactNode;
  customDateInterval?: string;
};

function getValueFormatter(usageType: UsageType): (value: number) => string {
  switch (usageType) {
    case UsageType.Storage:
    case UsageType.Weave:
      return value => `${numeral(value).format('0.0b')}`;
    case UsageType.TrackedHours:
      return value => `${d3.format(',')(secondsToHours(value))} hrs`;
    case UsageType.InferenceCost:
      return value => `${d3.format('$,.2f')(value / 100)}`;
    case UsageType.TrainingCost:
    case UsageType.WbAgentCost:
    case UsageType.SandboxesCost:
      return value => `${d3.format('$,.2f')(value / 100)}`;
    case UsageType.TrainingDuration:
      return value => {
        const seconds = millisecondsToSeconds(value);
        if (seconds < NUM_SECONDS_IN_HOUR) {
          const minutes = secondsToMinutes(seconds);
          return `${d3.format(',')(minutes)} GPU mins`;
        } else {
          const hours = secondsToHours(seconds);
          return `${d3.format(',')(hours)} GPU hrs`;
        }
      };
    case UsageType.UserSeats:
      return value => String(value);
    default:
      console.log('Unknown usage type: ', usageType);
      return value => `${value}`;
  }
}

export function getOverageChargeText(
  usageType: UsageType,
  isMonthly: boolean = false
): string {
  switch (usageType) {
    case UsageType.Storage:
      return 'Additional storage billed at $0.03/GB';
    case UsageType.Weave:
      return 'Additional data billed at $0.10/MB';
    case UsageType.TrackedHours:
      return 'Additional hours billed at $1 per hour';
    case UsageType.InferenceCost:
      return 'Additional inference cost listed [here](https://wandb.ai/site/pricing/inference)';
    case UsageType.TrainingCost:
      return '$2.70 per GPU/hour';
    case UsageType.WbAgentCost:
      return 'Additional WB Agent usage billed at cost';
    case UsageType.SandboxesCost:
      return 'Additional Sandboxes usage billed at cost';
    case UsageType.TrainingDuration:
      return 'Free for limited time';
    case UsageType.UserSeats:
      return isMonthly
        ? '$60.00 per user per month'
        : '$50.00 per user per month billed yearly';
    default:
      console.log('Unknown usage type: ', usageType);
      return 'Additional pricing information [here](https://wandb.ai/site/pricing)';
  }
}

export function getDateRangeText(date1: Date, date2: Date) {
  return `${getFormattedBillingDate(date1)} - ${getFormattedBillingDate(
    date2
  )}`;
}

/**
 * The inner content of a MeterSection with configurable styling.
 * Use this when you need custom styling control over the meter section.
 */
export const MeterSectionContent = ({
  className,
  title,
  billingPeriodStart,
  billingPeriodEnd,
  value,
  usageType,
  limit,
  button,
  isMonthly,
  isProPlan,
  hideDateInterval = false,
  costText,
  isUnlimited,
  secondaryButton,
  customDateInterval,
}: Props) => {
  const format = getValueFormatter(usageType);
  const fraction = limit != null ? value / limit : 1; // if there's no limit, then just show the bar as full
  return (
    <div className={className} data-test="meter-section">
      <div className="flex justify-between">
        <div className="font-semibold">{title}</div>
        {!hideDateInterval && (
          <div className="text-sm text-moon-500">
            {customDateInterval != null
              ? customDateInterval
              : `${
                  usageType === UsageType.Storage && isProPlan
                    ? 'Avg usage for'
                    : 'Usage for'
                }
            ${getDateRangeText(
              billingPeriodStart ?? DEFAULT_DAYS_AGO,
              billingPeriodEnd ?? TOMORROW
            )} UTC`}
          </div>
        )}
      </div>
      <div className="flex justify-between">
        <div className="mb-16 flex items-end">
          <div className="text-[32px] font-semibold leading-none">
            {format(value)}
          </div>
          {limit != null && (
            <div className="ml-6 flex font-semibold text-moon-500">
              <div className="mr-6">/</div>
              {format(limit)}
            </div>
          )}
        </div>
      </div>
      <MeterBar fraction={fraction} isUnlimited={isUnlimited} size="small" />
      <div className="mt-12 text-xs">
        <div className="flex items-center justify-between">
          <div className="flex">
            {button}
            {secondaryButton != null && (
              <div className="ml-4">{secondaryButton}</div>
            )}
          </div>
          {/*Only show overcharge text if Pro Plan or for training usage card*/}
          {(isProPlan ||
            usageType === UsageType.TrainingDuration ||
            usageType === UsageType.TrainingCost ||
            usageType === UsageType.WbAgentCost ||
            usageType === UsageType.SandboxesCost) && (
            <div className="flex items-center gap-2 text-sm text-moon-700">
              <span className="flex items-center gap-2">
                {costText}
                <Tooltip
                  content={
                    <ReactMarkdown components={customLinkRenderer}>
                      {getOverageChargeText(usageType, isMonthly ?? false)}
                    </ReactMarkdown>
                  }
                  position="bottom right"
                  trigger={<Icon name="info" className="ml-2" />}
                />
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * A meter section with default styling applied.
 * For custom styling, use MeterSectionContent directly with your own className.
 */
export const MeterSection = ({className, ...props}: Props) => {
  return (
    <MeterSectionContent
      {...props}
      className={clsx(METER_SECTION_STYLE, className)}
    />
  );
};
